import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { clearSelectedProduct, fetchProductById } from '../features/productsSlice';
import { addToCart, addToGuestCart } from '../features/cartSlice';
import { addToWishlist, removeFromWishlist } from '../features/wishlistSlice';
import StatusMessage from '../components/common/StatusMessage';
import ProductDetail from '../components/products/ProductDetail';
import ProductRail from '../components/products/ProductRail';
import ProductReviews from '../components/products/ProductReviews';
import { apiClient } from '../apiClient';
import { API_ENDPOINTS, ROUTES } from '../constants';
import { Product } from '../types';
import { loginWithRedirect } from '../utils/navigation';
import { getRecentlyViewedIds, trackProductView } from '../utils/recentlyViewed';
import useDocumentTitle from '../hooks/useDocumentTitle';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { selectedProduct, loading, error } = useAppSelector(
    (state) => state.products
  );
  const { token } = useAppSelector((state) => state.auth);
  const wishlistedIds = useAppSelector((state) =>
    new Set((state.wishlist.wishlist?.items || []).map((item) => item.product?._id))
  );
  const cartProductIds = useAppSelector((state) => {
    const serverIds = (state.cart.cart?.items || []).map((item) => item.product?._id);
    const guestIds = state.cart.guestItems.map((item) => item.product?._id);
    return new Set([...serverIds, ...guestIds]);
  });

  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [recentlyViewed, setRecentlyViewed] = useState<Product[]>([]);

  useDocumentTitle(selectedProduct?.name, selectedProduct?.description?.slice(0, 160));

  useEffect(() => {
    if (id) {
      dispatch(fetchProductById(id));
      apiClient
        .get(`${API_ENDPOINTS.PRODUCTS}/${id}/related`)
        .then((res) => setRelatedProducts(res.data.products || []))
        .catch(() => setRelatedProducts([]));
    }

    return () => {
      dispatch(clearSelectedProduct());
      setRelatedProducts([]);
    };
  }, [dispatch, id]);

  // Track the view (needs the resolved product — the URL may be a slug)
  // and load the visitor's other recently viewed pieces
  useEffect(() => {
    if (!selectedProduct?._id) return;
    const previouslyViewed = getRecentlyViewedIds().filter(
      (viewedId) => viewedId !== selectedProduct._id
    );
    trackProductView(selectedProduct._id);
    if (previouslyViewed.length === 0) {
      setRecentlyViewed([]);
      return;
    }
    apiClient
      .get(API_ENDPOINTS.PRODUCTS, {
        params: { ids: previouslyViewed.join(','), limit: previouslyViewed.length }
      })
      .then((response) => {
        const products = (response.data.products || []) as Product[];
        const byId = new Map(products.map((p) => [p._id, p]));
        setRecentlyViewed(
          previouslyViewed.map((viewedId) => byId.get(viewedId)).filter(Boolean) as Product[]
        );
      })
      .catch(() => setRecentlyViewed([]));
  }, [selectedProduct?._id]);

  if (loading || !selectedProduct) {
    return <StatusMessage variant="loading" />;
  }

  if (error) {
    return <StatusMessage variant="error" message={error} />;
  }

  const handleRelatedAddToCart = async (product: Product) => {
    if (!token) {
      dispatch(addToGuestCart({ product, quantity: 1 }));
      return;
    }
    await dispatch(addToCart({ productId: product._id, quantity: 1 }));
  };

  const handleRelatedToggleWishlist = async (product: Product) => {
    if (!token) {
      navigate(loginWithRedirect(`/products/${product._id}`));
      return;
    }
    if (wishlistedIds.has(product._id)) {
      await dispatch(removeFromWishlist({ productId: product._id }));
      return;
    }
    await dispatch(addToWishlist({ productId: product._id }));
  };

  return (
    <Box>
    <ProductDetail
      product={selectedProduct}
      isWishlisted={wishlistedIds.has(selectedProduct._id)}
      isInCart={cartProductIds.has(selectedProduct._id)}
      onAddToCart={async () => {
        if (!token) {
          dispatch(addToGuestCart({ product: selectedProduct, quantity: 1 }));
          return;
        }
        await dispatch(addToCart({ productId: selectedProduct._id, quantity: 1 }));
      }}
      onGoToCart={() => {
        navigate(ROUTES.CART);
      }}
      onBuyNow={async () => {
        if (!token) {
          dispatch(addToGuestCart({ product: selectedProduct, quantity: 1 }));
          navigate(ROUTES.CHECKOUT);
          return;
        }
        await dispatch(addToCart({ productId: selectedProduct._id, quantity: 1 }));
        navigate(ROUTES.CHECKOUT);
      }}
      onToggleWishlist={async () => {
        if (!token) {
          navigate(loginWithRedirect(`/products/${selectedProduct._id}`));
          return;
        }
        if (wishlistedIds.has(selectedProduct._id)) {
          await dispatch(removeFromWishlist({ productId: selectedProduct._id }));
          return;
        }
        await dispatch(addToWishlist({ productId: selectedProduct._id }));
      }}
    />

    <ProductReviews
      productId={selectedProduct._id}
      ratingAverage={selectedProduct.ratingAverage}
      ratingCount={selectedProduct.ratingCount}
    />

    {relatedProducts.length > 0 && (
      <Box sx={{ mt: 6 }}>
        <ProductRail
          title="You may also like"
          subtitle="More from this collection"
          products={relatedProducts}
          onAddToCart={handleRelatedAddToCart}
          onAddToWishlist={handleRelatedToggleWishlist}
        />
      </Box>
    )}

    {recentlyViewed.length > 0 && (
      <Box sx={{ mt: relatedProducts.length > 0 ? 0 : 6 }}>
        <ProductRail
          title="Recently viewed"
          subtitle="Pick up where you left off."
          products={recentlyViewed}
          onAddToCart={handleRelatedAddToCart}
          onAddToWishlist={handleRelatedToggleWishlist}
        />
      </Box>
    )}
    </Box>
  );
};

export default ProductDetailPage;

